/**
 * Defense-in-depth HTML sanitizer for rendered notes documents.
 *
 * Trust model:
 *   - Strict-parse + Zod schema (`notesDocumentSchema`) is the primary boundary
 *     — it rejects unknown blocks, unknown marks, disallowed URL schemes, and
 *     unknown attrs are silently stripped via `.strip()`.
 *   - Tiptap's `generateHTML` is a serializer, not a sanitizer. Any drift in
 *     the schema (newly added attrs, future extensions, custom marks slipping
 *     past validation) becomes a DOM injection vector the moment its output
 *     reaches React's HTML-injection prop.
 *   - This module is the second wall: a closed-allowlist DOMPurify pass that
 *     explicitly enumerates the tags + attrs the notes editor's TipTap surface
 *     emits via `generateHTML(notesBaseExtensions)`. Anything outside the
 *     allowlist — including future schema additions — is dropped, not
 *     rendered. That tradeoff is intentional: a missing list-item style is
 *     a visible regression caught in review; an unsanitized `<script>` tag
 *     from AI-produced `previewDocJson` is a silent XSS in production.
 *
 * Allowlist provenance:
 *   Every entry maps to actual TipTap output. The set was generated by
 *   running `generateHTML(notesBaseExtensions)` against a doc covering every
 *   node + mark in `TOP_LEVEL_BLOCK_NODE_TYPES` and `MARK_NODE_TYPES`,
 *   including TaskList/TaskItem (which emits `<label>`, `<input>`, `<span>`,
 *   `<div>` plus `data-type`/`data-checked`). Reviewers maintaining the
 *   editor schema should re-run that exercise after adding any extension.
 */

import DOMPurify from "isomorphic-dompurify";

/**
 * Tags TipTap's `notesBaseExtensions` produces via `generateHTML`. Every
 * entry has a corresponding emit site — see the JSDoc above for how to
 * verify when adding/removing extensions.
 */
const ALLOWED_TAGS: readonly string[] = [
  // Block elements
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "blockquote",
  "ul",
  "ol",
  "li",
  "pre",
  "code",
  "hr",
  // Task list scaffolding (TaskItem renders <label><input/><span/></label><div>...)
  "label",
  "input",
  "span",
  "div",
  // Inline marks
  "strong",
  "em",
  "s",
  "br",
  "a",
];

/**
 * Attributes the TipTap render layer actually emits. Anything else is a
 * sign of either (a) an attacker-controlled attr that snuck past Zod, or
 * (b) a new extension that needs explicit review before being allowlisted.
 *
 * `class` is permitted because TipTap's CodeBlock renders
 * `<code class="language-${lang}">` and the language string is constrained
 * by the Zod schema (`z.string().nullable().optional()` on `language`).
 * `style` is deliberately omitted — no editor extension emits inline styles,
 * and allowing it would give attackers a CSS-injection sidechannel.
 */
const ALLOWED_ATTR: readonly string[] = [
  // Link mark
  "href",
  "rel",
  "target",
  // Code-block language class + task-item structural classes
  "class",
  // Ordered list numbering offset (TipTap emits <ol start="N"> when N != 1)
  "start",
  // Task list / task item
  "type", // <input type="checkbox">
  "checked",
  "data-type",
  "data-checked",
  // Stable per-block identity minted by BlockIdExtension
  "data-block-id",
];

/**
 * Sanitize the output of `notesDocumentToHtml` (or any HTML produced by the
 * notes editor's TipTap extensions) before assigning it to a React HTML-
 * injection prop. Returns sanitized HTML; the allowlist is closed — anything
 * not in `ALLOWED_TAGS` / `ALLOWED_ATTR` is dropped.
 *
 * Safe to call from both server (RSC, SSR) and client (hydration, useMemo)
 * because `isomorphic-dompurify` plugs into a JSDOM-backed sanitizer at
 * runtime when no real DOM is present.
 */
export function sanitizeRenderedNotesHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...ALLOWED_TAGS],
    ALLOWED_ATTR: [...ALLOWED_ATTR],
    // Pin `style` to forbidden so a future config change can't reopen the
    // CSS-injection surface silently. DOMPurify defaults already strip
    // event handlers (`on*=`).
    FORBID_ATTR: ["style"],
  });
}
